import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GameSettings {
  scoringSystem: 'side-out' | 'rally';
  playerMode: 'singles' | 'doubles';
  winningScore: number;
}

interface GameState {
  team1Score: number;
  team2Score: number;
  servingTeam: 1 | 2;
  servingPlayer: 1 | 2; // For doubles
  gamePhase: 'setup' | 'playing' | 'finished';
  winner: 1 | 2 | null;
}

interface GameHistory {
  id: number;
  timestamp: string;
  action: string;
  team1Score: number;
  team2Score: number;
  servingTeam: 1 | 2;
  servingPlayer: 1 | 2;
}

const Scoreboard: React.FC = () => {
  const { t } = useTranslation('common');
  
  const [settings, setSettings] = useState<GameSettings>({
    scoringSystem: 'side-out',
    playerMode: 'singles',
    winningScore: 11
  });

  const [gameState, setGameState] = useState<GameState>({
    team1Score: 0,
    team2Score: 0,
    servingTeam: 1,
    servingPlayer: 2, // Start with player 2 for doubles side-out (0-0-2)
    gamePhase: 'setup',
    winner: null
  });

  const [players, setPlayers] = useState({
    team1: { player1: '', player2: '' },
    team2: { player1: '', player2: '' }
  });

  const [gameHistory, setGameHistory] = useState<GameHistory[]>([]);
  const [showRules, setShowRules] = useState(false);
  const [customScore, setCustomScore] = useState('');

  // Check win condition
  const checkWinCondition = (team1Score: number, team2Score: number): 1 | 2 | null => {
    const scoreDiff = Math.abs(team1Score - team2Score);
    if (team1Score >= settings.winningScore && scoreDiff >= 2) return 1;
    if (team2Score >= settings.winningScore && scoreDiff >= 2) return 2;
    return null;
  };

  // Add point logic
  const addPoint = (team: 1 | 2) => {
    if (gameState.gamePhase !== 'playing') return;

    // Side-out scoring: only serving team can score
    if (settings.scoringSystem === 'side-out' && team !== gameState.servingTeam) {
      // Switch server without scoring
      switchServer();
      return;
    }

    const newState = { ...gameState };
    
    if (team === 1) {
      newState.team1Score++;
    } else {
      newState.team2Score++;
    }

    // Check for win
    const winner = checkWinCondition(newState.team1Score, newState.team2Score);
    if (winner) {
      newState.winner = winner;
      newState.gamePhase = 'finished';
    } else {
      // In rally scoring, switch server if non-serving team scored
      if (settings.scoringSystem === 'rally' && team !== gameState.servingTeam) {
        if (settings.playerMode === 'doubles') {
          newState.servingTeam = team;
          newState.servingPlayer = 1;
        } else {
          newState.servingTeam = team;
        }
      }
      // In side-out scoring, serving team keeps serving when they score
    }

    // Add to history
    const historyEntry: GameHistory = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString(),
      action: `Team ${team} scored`,
      team1Score: newState.team1Score,
      team2Score: newState.team2Score,
      servingTeam: newState.servingTeam,
      servingPlayer: newState.servingPlayer
    };

    setGameHistory(prev => [historyEntry, ...prev]);
    setGameState(newState);
  };

  // Switch server
  const switchServer = () => {
    if (gameState.gamePhase !== 'playing') return;

    const newState = { ...gameState };

    if (settings.playerMode === 'doubles') {
      if (newState.servingPlayer === 1) {
        newState.servingPlayer = 2;
      } else {
        newState.servingTeam = newState.servingTeam === 1 ? 2 : 1;
        newState.servingPlayer = 1;
      }
    } else {
      newState.servingTeam = newState.servingTeam === 1 ? 2 : 1;
    }

    const historyEntry: GameHistory = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString(),
      action: 'Server switched',
      team1Score: newState.team1Score,
      team2Score: newState.team2Score,
      servingTeam: newState.servingTeam,
      servingPlayer: newState.servingPlayer
    };

    setGameHistory(prev => [historyEntry, ...prev]);
    setGameState(newState);
  };

  // Undo last action
  const undoLastAction = () => {
    if (gameHistory.length === 0) return;

    const lastAction = gameHistory[0];
    setGameState({
      team1Score: lastAction.team1Score,
      team2Score: lastAction.team2Score,
      servingTeam: lastAction.servingTeam,
      servingPlayer: lastAction.servingPlayer,
      gamePhase: 'playing',
      winner: null
    });

    setGameHistory(prev => prev.slice(1));
  };

  // Start new game
  const startNewGame = () => {
    setGameState({
      team1Score: 0,
      team2Score: 0,
      servingTeam: 1,
      servingPlayer: settings.playerMode === 'doubles' && settings.scoringSystem === 'side-out' ? 2 : 1, // Special rule for doubles side-out
      gamePhase: 'playing',
      winner: null
    });
    setGameHistory([]);
  };

  // Reset to setup
  const resetToSetup = () => {
    setGameState({
      team1Score: 0,
      team2Score: 0,
      servingTeam: 1,
      servingPlayer: settings.playerMode === 'doubles' && settings.scoringSystem === 'side-out' ? 2 : 1, // Special rule for doubles side-out
      gamePhase: 'setup',
      winner: null
    });
    setGameHistory([]);
    setPlayers({
      team1: { player1: '', player2: '' },
      team2: { player1: '', player2: '' }
    });
  };

  // Start game from setup
  const startGame = () => {
    // Validate player names
    if (settings.playerMode === 'singles') {
      if (!players.team1.player1 || !players.team2.player1) {
        alert('Please enter player names for both teams');
        return;
      }
    } else {
      if (!players.team1.player1 || !players.team1.player2 || !players.team2.player1 || !players.team2.player2) {
        alert('Please enter all player names for doubles');
        return;
      }
    }

    setGameState(prev => ({ 
      ...prev, 
      gamePhase: 'playing',
      servingPlayer: settings.playerMode === 'doubles' && settings.scoringSystem === 'side-out' ? 2 : 1 // Special rule for doubles side-out
    }));
  };

  // Update scoring system and adjust default winning score
  const updateScoringSystem = (system: 'side-out' | 'rally') => {
    setSettings(prev => ({
      ...prev,
      scoringSystem: system,
      winningScore: system === 'side-out' ? 11 : 15
    }));
  };

  // Get score display for doubles side-out format
  const getScoreDisplay = () => {
    if (settings.playerMode === 'doubles' && settings.scoringSystem === 'side-out') {
      return `${gameState.team1Score}-${gameState.team2Score}-${gameState.servingPlayer}`;
    }
    return `${gameState.team1Score} - ${gameState.team2Score}`;
  };

  if (showRules) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-teal-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-gray-900">{t('pickleball_scoring_rules', 'Pickleball Scoring Rules')}</h1>
              <button
                onClick={() => setShowRules(false)}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-2"></i>
                {t('close', 'Close')}
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-blue-900 mb-3">{t('side_out_scoring', 'Side-Out Scoring (Traditional)')}</h2>
                <ul className="space-y-2 text-blue-800">
                  <li>{t('only_serving_team', '• Only the serving team can score points')}</li>
                  <li>{t('games_to_11', '• Games typically played to 11 points')}</li>
                  <li>{t('win_by_2', '• Must win by 2 points')}</li>
                  <li>{t('server_switches', '• Server switches when serving team loses rally')}</li>
                  <li>{t('doubles_each_serves', '• In doubles: Each player serves before switching teams')}</li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-green-900 mb-3">{t('rally_scoring_desc', 'Rally Scoring')}</h2>
                <ul className="space-y-2 text-green-800">
                  <li>{t('either_team_scores', '• Either team can score on any rally')}</li>
                  <li>{t('games_to_15', '• Games typically played to 15 points')}</li>
                  <li>{t('win_by_2', '• Must win by 2 points')}</li>
                  <li>{t('server_switches_each', '• Server switches after each point')}</li>
                  <li>{t('faster_paced', '• Faster-paced games')}</li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-purple-900 mb-3">{t('serving_rules', 'Serving Rules')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-purple-800 mb-2">{t('singles', 'Singles')}</h3>
                    <ul className="space-y-1 text-purple-700 text-sm">
                      <li>{t('server_alternates', '• Server alternates between teams')}</li>
                      <li>{t('serve_right_even', '• Serve from right when score is even')}</li>
                      <li>{t('serve_left_odd', '• Serve from left when score is odd')}</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-purple-800 mb-2">{t('doubles', 'Doubles')}</h3>
                    <ul className="space-y-1 text-purple-700 text-sm">
                      <li>{t('both_players_serve', '• Both players serve before switching teams')}</li>
                      <li>{t('score_format', '• Score format: Team1-Team2-Server')}</li>
                      <li>{t('first_serve_002', '• First serve of game starts at 0-0-2')}</li>
                      <li>{t('prevents_advantage', '• This prevents first team advantage')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-teal-500 to-blue-600">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <h1 className="text-4xl font-bold text-white">{t('pickleball_score', 'Pickleball Score')}</h1>
          </div>
          <button
            onClick={() => setShowRules(true)}
            className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-question-line mr-2"></i>
            {t('rules', 'Rules')}
          </button>
        </div>

        {gameState.gamePhase === 'setup' && (
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
            <h2 className="text-2xl font-semibold mb-6 text-gray-900">{t('game_configuration', 'Game Configuration')}</h2>
            
            {/* Scoring System */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">{t('scoring_system', 'Scoring System')}</label>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => updateScoringSystem('side-out')}
                  className={`px-6 py-3 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                    settings.scoringSystem === 'side-out'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('side_out_traditional', 'Side-Out (Traditional)')}
                </button>
                <button
                  onClick={() => updateScoringSystem('rally')}
                  className={`px-6 py-3 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                    settings.scoringSystem === 'rally'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('rally_scoring', 'Rally Scoring')}
                </button>
              </div>
            </div>

            {/* Player Mode */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">{t('player_mode', 'Player Mode')}</label>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setSettings(prev => ({ ...prev, playerMode: 'singles' }))}
                  className={`px-6 py-3 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                    settings.playerMode === 'singles'
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('singles_1v1', 'Singles (1v1)')}
                </button>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, playerMode: 'doubles' }))}
                  className={`px-6 py-3 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                    settings.playerMode === 'doubles'
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('doubles_2v2', 'Doubles (2v2)')}
                </button>
              </div>
            </div>

            {/* Winning Score */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">{t('winning_score', 'Winning Score')}</label>
              <div className="flex flex-wrap gap-3 mb-3">
                {[11, 15, 21].map(score => (
                  <button
                    key={score}
                    onClick={() => setSettings(prev => ({ ...prev, winningScore: score }))}
                    className={`px-4 py-2 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                      settings.winningScore === score
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {score} {t('points', 'points')}
                  </button>
                ))}
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    placeholder={t('custom', 'Custom')}
                    value={customScore}
                    onChange={(e) => setCustomScore(e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={() => {
                      const score = parseInt(customScore);
                      if (score >= 1 && score <= 50) {
                        setSettings(prev => ({ ...prev, winningScore: score }));
                        setCustomScore('');
                      }
                    }}
                    className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    {t('set', 'Set')}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {t('current_points', 'Current:')} {settings.winningScore} {t('must_win_by_2', 'points (must win by 2)')}
              </p>
            </div>

            {/* Player Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">{t('team_1', 'Team 1')}</h3>
                <input
                  type="text"
                  placeholder={t('player_1_name', 'Player 1 name')}
                  value={players.team1.player1}
                  onChange={(e) => setPlayers(prev => ({
                    ...prev,
                    team1: { ...prev.team1, player1: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-3"
                />
                {settings.playerMode === 'doubles' && (
                  <input
                    type="text"
                    placeholder={t('player_2_name', 'Player 2 name')}
                    value={players.team1.player2}
                    onChange={(e) => setPlayers(prev => ({
                      ...prev,
                      team1: { ...prev.team1, player2: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                )}
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">{t('team_2', 'Team 2')}</h3>
                <input
                  type="text"
                  placeholder={t('player_1_name', 'Player 1 name')}
                  value={players.team2.player1}
                  onChange={(e) => setPlayers(prev => ({
                    ...prev,
                    team2: { ...prev.team2, player1: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-3"
                />
                {settings.playerMode === 'doubles' && (
                  <input
                    type="text"
                    placeholder={t('player_2_name', 'Player 2 name')}
                    value={players.team2.player2}
                    onChange={(e) => setPlayers(prev => ({
                      ...prev,
                      team2: { ...prev.team2, player2: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>

            <button
              onClick={startGame}
              className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white py-4 px-6 rounded-lg hover:from-teal-700 hover:to-blue-700 transition-all text-lg font-medium whitespace-nowrap cursor-pointer"
            >
              {t('start_game', 'Start Game')}
            </button>
          </div>
        )}

        {(gameState.gamePhase === 'playing' || gameState.gamePhase === 'finished') && (
          <div className="space-y-6">
            {/* Score Display */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
              <div className="text-center mb-6">
                <div className="text-6xl font-bold text-gray-900 mb-2">
                  {getScoreDisplay()}
                </div>
                <div className="text-lg text-gray-600">
                  {settings.scoringSystem === 'side-out' ? t('side_out_traditional', 'Side-Out Scoring') : t('rally_scoring', 'Rally Scoring')} • 
                  {settings.playerMode === 'singles' ? ` ${t('singles', 'Singles')}` : ` ${t('doubles', 'Doubles')}`} • 
                  First to {settings.winningScore}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                {/* Team 1 */}
                <div className={`text-center p-3 sm:p-4 lg:p-6 rounded-xl transition-all ${
                  gameState.servingTeam === 1 ? 'bg-blue-100 border-4 border-blue-500' : 'bg-gray-50'
                }`}>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t('team_1', 'Team 1')}</h3>
                  <p className="text-gray-700 mb-1 text-sm sm:text-base truncate">{players.team1.player1}</p>
                  {settings.playerMode === 'doubles' && (
                    <p className="text-gray-700 mb-4 text-sm sm:text-base truncate">{players.team1.player2}</p>
                  )}
                  
                  <div className="text-4xl sm:text-6xl lg:text-8xl font-bold text-blue-600 mb-4">{gameState.team1Score}</div>
                  
                  {gameState.gamePhase === 'playing' && (
                    <button
                      onClick={() => addPoint(1)}
                      className="bg-blue-600 text-white px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base lg:text-lg font-medium whitespace-nowrap cursor-pointer w-full"
                    >
                      {t('add_point', 'Add Point')}
                    </button>
                  )}
                  
                  {gameState.servingTeam === 1 && (
                    <div className="mt-4 text-blue-600 font-semibold text-sm sm:text-base">
                      <i className="ri-play-circle-fill mr-2"></i>
                      {t('serving', 'Serving')}
                      {settings.playerMode === 'doubles' && ` (${gameState.servingPlayer === 1 ? players.team1.player1 : players.team1.player2})`}
                    </div>
                  )}
                </div>

                {/* Team 2 */}
                <div className={`text-center p-3 sm:p-4 lg:p-6 rounded-xl transition-all ${
                  gameState.servingTeam === 2 ? 'bg-green-100 border-4 border-green-500' : 'bg-gray-50'
                }`}>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t('team_2', 'Team 2')}</h3>
                  <p className="text-gray-700 mb-1 text-sm sm:text-base truncate">{players.team2.player1}</p>
                  {settings.playerMode === 'doubles' && (
                    <p className="text-gray-700 mb-4 text-sm sm:text-base truncate">{players.team2.player2}</p>
                  )}
                  
                  <div className="text-4xl sm:text-6xl lg:text-8xl font-bold text-green-600 mb-4">{gameState.team2Score}</div>
                  
                  {gameState.gamePhase === 'playing' && (
                    <button
                      onClick={() => addPoint(2)}
                      className="bg-green-600 text-white px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base lg:text-lg font-medium whitespace-nowrap cursor-pointer w-full"
                    >
                      {t('add_point', 'Add Point')}
                    </button>
                  )}
                  
                  {gameState.servingTeam === 2 && (
                    <div className="mt-4 text-green-600 font-semibold text-sm sm:text-base">
                      <i className="ri-play-circle-fill mr-2"></i>
                      {t('serving', 'Serving')}
                      {settings.playerMode === 'doubles' && ` (${gameState.servingPlayer === 1 ? players.team2.player1 : players.team2.player2})`}
                    </div>
                  )}
                </div>
              </div>

              {/* Game Finished */}
              {gameState.gamePhase === 'finished' && (
                <div className="text-center mt-8 p-6 bg-yellow-50 rounded-xl">
                  <i className="ri-trophy-line text-6xl text-yellow-500 mb-4"></i>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {t('team_1', 'Team')} {gameState.winner} {t('wins', 'Wins!')}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {t('final_score', 'Final Score:')} {gameState.team1Score} - {gameState.team2Score}
                  </p>
                  <button
                    onClick={startNewGame}
                    className="bg-yellow-600 text-white px-8 py-3 rounded-lg hover:bg-yellow-700 transition-colors text-lg font-medium whitespace-nowrap cursor-pointer"
                  >
                    {t('new_game', 'New Game')}
                  </button>
                </div>
              )}
            </div>

            {/* Game Controls */}
            {gameState.gamePhase === 'playing' && (
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                <div className="flex flex-wrap gap-4 justify-center">
                  {settings.playerMode === 'doubles' && (
                    <button
                      onClick={switchServer}
                      className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-repeat-line mr-2"></i>
                      {t('switch_server', 'Switch Server')}
                    </button>
                  )}
                  
                  <button
                    onClick={undoLastAction}
                    disabled={gameHistory.length === 0}
                    className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                      gameHistory.length > 0
                        ? 'bg-orange-600 text-white hover:bg-orange-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <i className="ri-arrow-go-back-line mr-2"></i>
                    {t('undo', 'Undo')}
                  </button>
                  
                  <button
                    onClick={startNewGame}
                    className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    {t('new_game', 'New Game')}
                  </button>
                  
                  <button
                    onClick={resetToSetup}
                    className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-settings-line mr-2"></i>
                    {t('setup', 'Setup')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Scoreboard;
